import { useQueries } from '@tanstack/react-query';

type Where = Record<string, unknown>;

/**
 * How many items each queue of a review list holds (the counts of the Component Map's queue status
 * switch and of tabs with waiting work). One small query per queue: `fetchTotal` reads the list with
 * `base` plus the queue's own filter and returns its total.
 *
 * Every count is keyed under the list's own `queryKey` prefix, so the invalidation its mutations already
 * do (approve, reject, archive, ...) refreshes the counts too.
 */
export const useQueueCounts = <TValue extends string>({
  queryKey,
  base,
  queues,
  fetchTotal,
  enabled = true,
}: {
  queryKey: readonly unknown[];
  base: Where;
  queues: readonly { value: TValue; filter: Where }[];
  fetchTotal: (filter: Where) => Promise<number>;
  enabled?: boolean;
}): Partial<Record<TValue, number>> => {
  const results = useQueries({
    queries: queues.map((queue) => {
      const filter = { ...base, ...queue.filter };
      return {
        queryKey: [...queryKey, 'count', queue.value, filter],
        queryFn: () => fetchTotal(filter),
        enabled,
        staleTime: 60 * 1000,
      };
    }),
  });
  return Object.fromEntries(
    queues.map((queue, index) => [queue.value, results[index]?.data])
  ) as Partial<Record<TValue, number>>;
};
